import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconArrowLeft,
  IconBrain,
  IconCloud,
  IconDatabase,
  IconPlug,
  IconPuzzle,
  IconRobot,
  IconStethoscope,
  IconWorld,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import React from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { HostedAssistantManager } from '../components/HostedAssistantManager'
import { useOwlbertWebAuth } from '../auth/OwlbertWebAuthContext'
import { WebHostedKnowledgeSettingsPanel } from './settings/WebHostedKnowledgeSettingsPanel'
import { WebHostedLlmSettingsPanel } from './settings/WebHostedLlmSettingsPanel'
import { WebMcpServersSettingsPanel } from './settings/WebMcpServersSettingsPanel'
import { WebModulesSettingsPanel } from './settings/WebModulesSettingsPanel'

const SETTINGS_SECTION_STORAGE_KEY = 'owlbert_web_settings_section'

type SettingsSectionKey =
  | 'general'
  | 'assistants'
  | 'knowledge'
  | 'llms'
  | 'mcp'
  | 'modules'
  | 'contextSources'
  | 'diagnostics'

type SettingsSection = {
  key: SettingsSectionKey
  label: string
  description: string
  icon: React.ReactNode
}

const BASE_SECTIONS: SettingsSection[] = [
  {
    key: 'general',
    label: 'General',
    description: 'Server connection, sign-in, and workspace',
    icon: <IconWorld size={24} />,
  },
  {
    key: 'assistants',
    label: 'Assistants',
    description: 'Create and edit cloud assistants',
    icon: <IconRobot size={24} />,
  },
]

const HOSTED_LLM_KNOWLEDGE_SECTIONS: SettingsSection[] = [
  {
    key: 'knowledge',
    label: 'Knowledge',
    description: 'Knowledgebase, embedder, and vector database',
    icon: <IconDatabase size={24} />,
  },
  {
    key: 'llms',
    label: 'LLMs',
    description: 'Language model providers and defaults',
    icon: <IconBrain size={24} />,
  },
]

const PRO_ONLY_SECTIONS: SettingsSection[] = [
  {
    key: 'mcp',
    label: 'MCP servers',
    description: 'Model Context Protocol servers for tools',
    icon: <IconPlug size={24} />,
  },
  {
    key: 'modules',
    label: 'Modules',
    description: 'Enable or disable workspace modules',
    icon: <IconPuzzle size={24} />,
  },
  {
    key: 'contextSources',
    label: 'Context sources',
    description: 'Extra context for chat and assistants',
    icon: <IconCloud size={24} />,
  },
  {
    key: 'diagnostics',
    label: 'Diagnostics',
    description: 'Background jobs and diagnostic tools',
    icon: <IconStethoscope size={24} />,
  },
]

/** Match `WebBasicShell` — below this width settings uses card index → detail + `/settings/:sectionKey`. */
const MOBILE_SETTINGS_STACK_QUERY = '(max-width: 47.99em)'

function readStoredSection(allowed: Set<SettingsSectionKey>): SettingsSectionKey {
  try {
    const raw = window.localStorage.getItem(SETTINGS_SECTION_STORAGE_KEY)
    if (raw && allowed.has(raw as SettingsSectionKey)) return raw as SettingsSectionKey
  } catch {
    /* ignore */
  }
  return 'general'
}

export const SettingsPage: React.FC = () => {
  const auth = useOwlbertWebAuth()
  const navigate = useNavigate()
  const { sectionKey: sectionKeyFromUrl } = useParams<{ sectionKey?: string }>()
  const isMobileSettingsStack = useMediaQuery(MOBILE_SETTINGS_STACK_QUERY, false, { getInitialValueInEffect: true })
  const [searchParams, setSearchParams] = useSearchParams()
  const sections = React.useMemo(() => {
    const parts: SettingsSection[] = [...BASE_SECTIONS]
    /** Match desktop `settings.tsx`: Knowledge, LLMs, MCP, modules, context, diagnostics require Pro. */
    if (auth.sessionReady && auth.isProMode) {
      parts.push(...HOSTED_LLM_KNOWLEDGE_SECTIONS)
      parts.push(...PRO_ONLY_SECTIONS)
    }
    return parts
  }, [auth.sessionReady, auth.isProMode])

  const allowedKeys = React.useMemo(() => new Set(sections.map((s) => s.key)), [sections])

  const [activeKey, setActiveKey] = React.useState<SettingsSectionKey>(() =>
    readStoredSection(new Set(BASE_SECTIONS.map((s) => s.key))),
  )

  React.useEffect(() => {
    if (!allowedKeys.has(activeKey)) setActiveKey('general')
  }, [allowedKeys, activeKey])

  const [busy, setBusy] = React.useState(false)
  const isDark = useComputedColorScheme() === 'dark'

  const panelFromUrl = searchParams.get('panel')

  React.useEffect(() => {
    if (panelFromUrl && allowedKeys.has(panelFromUrl as SettingsSectionKey)) {
      const k = panelFromUrl as SettingsSectionKey
      setActiveKey(k)
      navigate(`/settings/${k}`, { replace: true })
      setSearchParams({}, { replace: true })
    }
  }, [panelFromUrl, setSearchParams, allowedKeys, navigate])

  React.useEffect(() => {
    if (!sectionKeyFromUrl) return
    if (!allowedKeys.has(sectionKeyFromUrl as SettingsSectionKey)) {
      navigate('/settings', { replace: true })
    }
  }, [sectionKeyFromUrl, allowedKeys, navigate])

  React.useEffect(() => {
    if (sectionKeyFromUrl && allowedKeys.has(sectionKeyFromUrl as SettingsSectionKey)) {
      setActiveKey(sectionKeyFromUrl as SettingsSectionKey)
    }
  }, [sectionKeyFromUrl, allowedKeys])

  const selectSection = React.useCallback(
    (key: SettingsSectionKey) => {
      setActiveKey(key)
      try {
        window.localStorage.setItem(SETTINGS_SECTION_STORAGE_KEY, key)
      } catch {
        /* ignore */
      }
      navigate(`/settings/${key}`)
    },
    [navigate],
  )

  const onLogout = async () => {
    setBusy(true)
    try {
      await auth.logout()
      notifications.show({ color: 'green', message: 'Signed out.' })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Sign-out failed', message: String(e) })
    } finally {
      setBusy(false)
    }
  }

  const hoverBg = isDark ? 'var(--mantine-color-dark-5)' : 'var(--mantine-color-gray-1)'
  const unselectedIconColor = isDark ? 'var(--mantine-color-dark-2)' : 'var(--mantine-color-gray-6)'

  const renderNavItem = (section: SettingsSection) => {
    const onMobileIndex = isMobileSettingsStack && !sectionKeyFromUrl
    const isSelected = onMobileIndex ? false : activeKey === section.key
    return (
      <Box
        key={section.key}
        p={16}
        style={{
          cursor: 'pointer',
          borderRadius: 8,
          backgroundColor: isSelected ? 'var(--accent-selected-bg)' : 'transparent',
          border: isSelected ? '1px solid var(--accent-selected-border)' : '1px solid transparent',
          transition: 'all 0.2s',
        }}
        onClick={() => selectSection(section.key)}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = hoverBg
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'
        }}
      >
        <Group gap={14} wrap="nowrap" align="flex-start">
          <Box
            style={{
              color: isSelected ? 'var(--accent-selected-icon)' : unselectedIconColor,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {section.icon}
          </Box>
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={700} style={isSelected ? { color: 'var(--accent-selected-text)' } : undefined}>
              {section.label}
            </Text>
            <Text
              size="xs"
              lineClamp={2}
              c={isSelected ? undefined : 'dimmed'}
              style={isSelected ? { color: 'var(--accent-selected-text)', opacity: 0.9 } : undefined}
            >
              {section.description}
            </Text>
          </Stack>
        </Group>
      </Box>
    )
  }

  const comingSoonPanel = (title: string, body: string) => (
    <Stack gap="md" maw={720}>
      <Paper withBorder p="md" radius="md">
        <Text fw={700}>{title}</Text>
        <Text size="sm" c="dimmed" mt="xs">
          {body}
        </Text>
      </Paper>
    </Stack>
  )

  const generalPanel = (
    <Stack gap="md" maw={720}>
      {auth.sessionReady && auth.entitlement && auth.entitlement.features.length > 0 ? (
        <Paper withBorder p="md" radius="md">
          <Stack gap="xs">
            <Text fw={700}>License features</Text>
            <Text size="sm" c="dimmed">
              Active on this account (from the server):
            </Text>
            <Stack gap={4}>
              {auth.entitlement.featureDetails.length ? (
                auth.entitlement.featureDetails.map((f) => (
                  <Text key={f.license_id} size="sm">
                    <Text span fw={600}>
                      {f.friendly_name}
                    </Text>{' '}
                    <Text span c="dimmed" size="xs">
                      ({f.license_id})
                    </Text>
                  </Text>
                ))
              ) : (
                <Text size="sm" c="dimmed">
                  {auth.entitlement.features.join(', ')}
                </Text>
              )}
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text fw={700}>Connection</Text>
          {auth.configured ? (
            <Text size="sm" c="dimmed">
              Server: {auth.backendUrl}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              This build is not linked to an Owlbert server. Ask whoever deployed it if you expected sign-in here.
            </Text>
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text fw={700}>Account</Text>
          {auth.error ? (
            <Alert color={auth.sessionReady ? 'orange' : 'red'} title={auth.sessionReady ? 'Data' : 'Sign-in'}>
              {auth.error}
            </Alert>
          ) : null}
          {auth.loading ? (
            <Text size="sm" c="dimmed">
              Checking session…
            </Text>
          ) : (
            <>
              <Text size="sm">
                Signed in as <b>{auth.user?.email ?? auth.user?.id}</b>
              </Text>
              {auth.workspaces.length ? (
                <Select
                  label="Workspace"
                  description="Which workspace to use for documents and chat."
                  data={auth.workspaces.map((w) => ({ value: w.id, label: w.name }))}
                  value={auth.workspaceId ?? undefined}
                  onChange={(v) => {
                    if (v) auth.setWorkspaceId(v)
                  }}
                />
              ) : (
                <Text size="sm" c="orange">
                  No workspaces on this account yet.
                </Text>
              )}
              <Group justify="flex-end">
                <Button variant="light" loading={busy} onClick={() => void onLogout()}>
                  Sign out
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  )

  const assistantsPanel = (
    <Stack gap="md" maw={720}>
      <Text size="sm" c="dimmed">
        Create and edit assistants here. Chats are on the{' '}
        <Link to="/assistants" style={{ fontWeight: 600 }}>
          Chat
        </Link>{' '}
        page.
      </Text>
      <HostedAssistantManager layout="settings" />
    </Stack>
  )

  const rightContent =
    activeKey === 'general'
      ? generalPanel
      : activeKey === 'assistants'
        ? assistantsPanel
        : activeKey === 'knowledge'
          ? <WebHostedKnowledgeSettingsPanel />
          : activeKey === 'llms'
            ? <WebHostedLlmSettingsPanel />
            : activeKey === 'mcp'
              ? <WebMcpServersSettingsPanel />
              : activeKey === 'modules'
                ? <WebModulesSettingsPanel />
                : activeKey === 'contextSources'
                  ? comingSoonPanel('Context sources', 'Attach context catalogs and sources for chat (coming soon).')
                  : activeKey === 'diagnostics'
                    ? comingSoonPanel('Diagnostics', 'Background jobs, logs, and health checks (coming soon).')
                    : generalPanel

  const activeSectionMeta = sections.find((s) => s.key === activeKey)

  const settingsNavColumn = (
    <Box
      style={{
        width: isMobileSettingsStack ? '100%' : 300,
        minWidth: isMobileSettingsStack ? 0 : 260,
        maxWidth: isMobileSettingsStack ? 'none' : 360,
        height: '100%',
        backgroundColor: 'var(--shell-main-bg)',
        borderRight: isMobileSettingsStack ? 'none' : '1px solid var(--separator-color)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: isMobileSettingsStack ? 1 : undefined,
        minHeight: 0,
      }}
    >
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
        <Stack gap="sm" p="md" pb="lg">
          {sections.map(renderNavItem)}
        </Stack>
      </ScrollArea>
    </Box>
  )

  const settingsDetailColumn = (
        <Box
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--shell-main-bg)',
          }}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
            <Box className="settings-content" pt="lg" px="lg" pb="xl" style={{ maxWidth: 960 }}>
              {rightContent}
            </Box>
          </ScrollArea>
        </Box>
  )

  const mobileSettingsDetailHeader = (
    <Box
      className="owlbert-web-mobile-stack-header"
      style={{
        flexShrink: 0,
        padding: '8px 12px',
        paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
        borderBottom: '1px solid var(--separator-color)',
        backgroundColor: 'var(--shell-main-bg)',
      }}
    >
      <Group gap="sm" wrap="nowrap" align="center">
        <ActionIcon
          variant="default"
          size="lg"
          radius="xl"
          aria-label="Back to settings"
          onClick={() => navigate('/settings')}
        >
          <IconArrowLeft size={20} stroke={1.5} />
        </ActionIcon>
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Title order={4} lineClamp={1}>
            {activeSectionMeta?.label ?? 'Settings'}
          </Title>
          {activeSectionMeta?.description ? (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {activeSectionMeta.description}
            </Text>
          ) : null}
        </Stack>
      </Group>
    </Box>
  )

  const showMobileSettingsIndex = isMobileSettingsStack && !sectionKeyFromUrl
  const showMobileSettingsDetail = isMobileSettingsStack && Boolean(sectionKeyFromUrl)

  return (
    <Box className="owlbert-web-page">
      {!showMobileSettingsDetail ? (
        <Box className="owlbert-web-page-header">
          <Title order={3}>Settings</Title>
          <Text c="dimmed" size="sm">
            Sign in, pick a workspace, and manage assistants.
          </Text>
        </Box>
      ) : null}

      <Flex style={{ flex: 1, minHeight: 0, width: '100%', minWidth: 0 }}>
        {showMobileSettingsIndex ? (
          settingsNavColumn
        ) : showMobileSettingsDetail ? (
          <Box
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            {mobileSettingsDetailHeader}
            {settingsDetailColumn}
          </Box>
        ) : (
          <>
            {settingsNavColumn}
            {settingsDetailColumn}
          </>
        )}
      </Flex>
    </Box>
  )
}
